import React, { useState, useEffect } from "react"
import ReactApexChart from "react-apexcharts"

const ActualScheduledDistribution = ({
	data,
	height = 300,
	chartHeight = 200,
	initialTreatment = null
}) => {
	const [selectedTreatment, setSelectedTreatment] = useState(initialTreatment)
	const [treatmentOptions, setTreatmentOptions] = useState([])
	const [distributionData, setDistributionData] = useState(null)
	const [debugInfo, setDebugInfo] = useState({
		dataLength: 0,
		treatmentOptionsLength: 0,
		selectedTreatment: null,
		allDurationsLength: 0
	})

	// Extract treatments from data
	useEffect(() => {
		if (!data || data.length === 0) return

		// Collect all unique treatments across all days
		const allTreatments = new Set()

		data.forEach(day => {
			if (day.treatment_duration_variance?.treatments) {
				day.treatment_duration_variance.treatments.forEach(treatment => {
					if (treatment.appointmentType) {
						allTreatments.add(treatment.appointmentType)
					} else if (treatment.name) {
						allTreatments.add(treatment.name)
					}
				})
			}
		})

		const options = Array.from(allTreatments)
		setTreatmentOptions(options)

		setDebugInfo(prev => ({
			...prev,
			dataLength: data.length,
			treatmentOptionsLength: options.length
		}))

		// Set initial treatment
		if (options.length > 0 && !selectedTreatment) {
			if (initialTreatment && options.includes(initialTreatment)) {
				setSelectedTreatment(initialTreatment)
			} else {
				setSelectedTreatment(options[0])
			}
		}
	}, [data, initialTreatment, selectedTreatment])

	// Generate distribution data when treatment selection changes
	useEffect(() => {
		if (!selectedTreatment || !data || data.length === 0) return

		setDebugInfo(prev => ({
			...prev,
			selectedTreatment
		}))

		// Collect durations for selected treatment
		const allDurations = []
		let scheduledDuration = 0
		let treatmentName = ""

		data.forEach(day => {
			if (day.treatment_duration_variance?.treatments) {
				const matchingTreatment = day.treatment_duration_variance.treatments.find(
					t => t.appointmentType === selectedTreatment || t.name === selectedTreatment
				)

				if (matchingTreatment) {
					scheduledDuration = matchingTreatment.scheduledDuration
					treatmentName = matchingTreatment.name || matchingTreatment.appointmentType

					if (matchingTreatment.actualDurations?.length > 0) {
						matchingTreatment.actualDurations.forEach(d => {
							if (typeof d.duration === "number") {
								allDurations.push(d.duration)
							} else if (typeof d === "number") {
								allDurations.push(d)
							}
						})
					}
				}
			}
		})

		// Update debug info with durations
		setDebugInfo(prev => ({
			...prev,
			allDurationsLength: allDurations.length
		}))

		if (allDurations.length === 0) {
			setDistributionData(null)
			return
		}

		// Create distribution buckets
		const bucketSize = 30
		const minDuration = Math.min(...allDurations)
		const maxDuration = Math.max(...allDurations)
		const bucketRange = Math.max(
			120,
			Math.ceil((maxDuration - minDuration) / bucketSize) * bucketSize
		)

		const center = scheduledDuration || Math.round((minDuration + maxDuration) / 2)
		const lowerBound = Math.max(0, center - bucketRange / 2)
		const upperBound = center + bucketRange / 2

		// Initialize buckets
		const buckets = {}
		for (let i = lowerBound; i <= upperBound; i += bucketSize) {
			buckets[i] = 0
		}

		// Fill buckets
		allDurations.forEach(duration => {
			// Find the appropriate bucket
			const bucketKey = Math.floor(duration / bucketSize) * bucketSize

			// Ensure we only count durations within our range
			if (bucketKey >= lowerBound && bucketKey <= upperBound) {
				buckets[bucketKey] = (buckets[bucketKey] || 0) + 1
			}
		})

		// Convert to percentage
		const totalPatients = allDurations.length

		// Calculate total count in all buckets
		// This ensures percentages add up to 100% even if some durations fell outside our range
		const totalInBuckets = Object.values(buckets).reduce((sum, count) => sum + count, 0)

		const percentageBuckets = Object.keys(buckets).map(key => ({
			x: parseInt(key),
			y: (buckets[key] / totalInBuckets) * 100
		}))

		// Calculate average and variance
		const sum = allDurations.reduce((acc, val) => acc + val, 0)
		const average = sum / totalPatients
		const variance = average - (scheduledDuration || 0)

		setDistributionData({
			series: percentageBuckets,
			scheduledDuration: scheduledDuration || Math.round(average),
			totalPatients,
			averageActual: average.toFixed(1),
			variance: variance.toFixed(1),
			treatmentName
		})
	}, [selectedTreatment, data])

	// Handle treatment selection change
	const handleTreatmentChange = e => {
		setSelectedTreatment(e.target.value)
	}

	// Prepare chart options
	const chartOptions = {
		chart: {
			type: "area",
			height: chartHeight,
			toolbar: {
				show: false
			},
			fontFamily: "Manrope, sans-serif"
		},
		dataLabels: {
			enabled: false
		},
		stroke: {
			curve: "smooth",
			width: 2,
			colors: ["#6955EA"] // Use the custom purple color
		},
		fill: {
			type: "gradient",
			gradient: {
				shadeIntensity: 1,
				opacityFrom: 0.7,
				opacityTo: 0.3,
				stops: [0, 100],
				colorStops: [
					{
						offset: 0,
						color: "#6955EA",
						opacity: 0.4
					},
					{
						offset: 100,
						color: "#FFFFFF",
						opacity: 0.1
					}
				]
			}
		},
		markers: {
			size: 0,
			colors: ["#6955EA"],
			strokeColors: "#fff"
		},
		xaxis: {
			type: "numeric",
			title: {
				text: "Duration (minutes)",
				style: {
					fontSize: "12px",
					fontFamily: "Manrope, sans-serif",
					color: "#8c8c8c"
				}
			},
			tickAmount: 5,
			labels: {
				formatter: val => `${val}m`,
				style: {
					fontSize: "12px",
					colors: ["#8c8c8c"]
				}
			},
			axisBorder: {
				show: false
			},
			axisTicks: {
				show: false
			}
		},
		yaxis: {
			title: {
				text: "Percentage of Patients",
				style: {
					fontSize: "12px",
					fontFamily: "Manrope, sans-serif",
					color: "#8c8c8c"
				}
			},
			labels: {
				formatter: val => `${val.toFixed(1)}%`,
				style: {
					fontSize: "12px",
					colors: ["#8c8c8c"]
				}
			},
			min: 0,
			axisBorder: {
				show: false
			},
			axisTicks: {
				show: false
			}
		},
		annotations: distributionData
			? {
					xaxis: [
						{
							x: distributionData.scheduledDuration,
							strokeDashArray: 5,
							borderColor: "#000000",
							label: {
								borderColor: "#000000",
								style: {
									color: "#fff",
									background: "#000000"
								}
							}
						}
					]
				}
			: {},
		tooltip: {
			x: {
				formatter: val => `${val} minutes`
			},
			y: {
				formatter: val => `${val.toFixed(1)}% of patients`
			},
			theme: "light",
			marker: {
				fillColors: ["#6955EA"]
			}
		},
		grid: {
			show: false
		}
	}

	const series = [
		{
			name: "Patient Distribution",
			data: distributionData?.series || []
		}
	]

	return (
		<div
			className="w-full bg-white rounded-lg shadow-lg p-4"
			style={{
				height: `${height}px`,
				overflow: "hidden",
				display: "flex",
				flexDirection: "column"
			}}
		>
			<div className="flex justify-between items-center mb-2">
				<h5 className="text-xl font-bold leading-none text-gray-900">
					Treatment Duration Distribution
				</h5>

				{treatmentOptions.length > 0 ? (
					<select
						value={selectedTreatment || ""}
						onChange={handleTreatmentChange}
						className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 w-fit pr-10 truncate"
					>
						{treatmentOptions.map(treatment => (
							<option key={treatment} value={treatment}>
								{treatment}
							</option>
						))}
					</select>
				) : null}
			</div>

			{distributionData ? (
				<>
					<div className="flex-grow" style={{ minHeight: 0 }}>
						<ReactApexChart
							options={chartOptions}
							series={series}
							type="area"
							height={chartHeight - 50}
						/>
					</div>
				</>
			) : (
				<div className="flex-grow flex flex-col items-center justify-center">
					<p className="text-gray-400 mb-4">
						{treatmentOptions.length > 0
							? "No data available for selected treatment"
							: "No treatment data available"}
					</p>
					<div className="text-xs text-left text-gray-400 p-2 bg-gray-100 rounded w-full max-w-md">
						<p>Debug Info:</p>
						<ul className="list-disc pl-5 mt-1">
							<li>Data Length: {debugInfo.dataLength}</li>
							<li>Treatment Options: {debugInfo.treatmentOptionsLength}</li>
							<li>Selected Treatment: {debugInfo.selectedTreatment || "None"}</li>
							<li>Durations Found: {debugInfo.allDurationsLength}</li>
						</ul>
					</div>
				</div>
			)}
		</div>
	)
}

export default ActualScheduledDistribution
